import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import {
  DashboardStats,
  DashboardCharts,
  DashboardIntelligence,
  WeeklyVolumePoint,
  BestEffort,
  CadencePoint,
  PaceHRPoint,
  LongRunPoint,
  WorkoutTypeDist,
  ConsistencyPoint,
  RaceConfidence,
} from '../../types';
import Navbar from '../Layout/Navbar';
import { useUnits } from '../../contexts/UnitsContext';
import ConfidenceBand from './ConfidenceBand';
import WeekPlanCard from './WeekPlanCard';
import './Dashboard.css';

// ─── helpers ───────────────────────────────────────────────────────────────

const fmtTime = (secs: number): string => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const fmtPace = (sec: number): string =>
  `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

const weekLabel = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const hrColor = (hr: number): string => {
  if (hr < 130) return '#22c55e';
  if (hr < 140) return '#84cc16';
  if (hr < 150) return '#eab308';
  if (hr < 160) return '#f97316';
  return '#ef4444';
};

// ─── SVG charts ────────────────────────────────────────────────────────────

const VolumeChart: React.FC<{ data: WeeklyVolumePoint[] }> = ({ data }) => {
  if (!data.length) return <p className="chart-empty">No data yet</p>;

  const W = 700, H = 220;
  const padL = 36, padR = 12, padT = 20, padB = 40;
  const cW = W - padL - padR;
  const cH = H - padT - padB;
  const n = data.length;
  const maxVal = Math.max(...data.map(d => Math.max(d.miles, d.plan_miles || 0)), 1);
  const maxY = maxVal * 1.18;
  const slotW = cW / n;
  const barW = Math.max(slotW * 0.62, 6);

  const toX = (i: number) => padL + i * slotW + (slotW - barW) / 2;
  const toY = (v: number) => padT + cH * (1 - v / maxY);

  // Y-axis ticks
  const step = maxY > 30 ? 10 : maxY > 15 ? 5 : 3;
  const yTicks: number[] = [];
  for (let v = 0; v <= maxY; v += step) yTicks.push(v);

  // Plan target polyline
  const planPoints = data
    .map((d, i) => d.plan_miles != null && d.plan_miles > 0
      ? `${toX(i) + barW / 2},${toY(d.plan_miles)}`
      : null)
    .filter(Boolean);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" aria-label="Weekly volume chart">
      {/* Grid */}
      {yTicks.map(t => (
        <g key={t}>
          <line x1={padL} x2={W - padR} y1={toY(t)} y2={toY(t)}
            stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
          <text x={padL - 4} y={toY(t) + 4} textAnchor="end"
            fill="rgba(255,255,255,0.28)" fontSize="9.5" fontFamily="inherit">
            {t}
          </text>
        </g>
      ))}

      {/* Baseline */}
      <line x1={padL} x2={W - padR} y1={padT + cH} y2={padT + cH}
        stroke="rgba(255,255,255,0.1)" strokeWidth="1" />

      {/* Plan target dotted line */}
      {planPoints.length > 1 && (
        <polyline
          points={planPoints.join(' ')}
          fill="none" stroke="#fc4c02" strokeWidth="1.5"
          strokeDasharray="4 3" opacity="0.7" />
      )}

      {/* Bars */}
      {data.map((d, i) => {
        const x = toX(i);
        const barH = Math.max(cH * (d.miles / maxY), 2);
        const y = toY(d.miles);
        const isCurrent = i === n - 1;

        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={barH}
              fill={isCurrent ? '#C9A84C' : 'rgba(201,168,76,0.42)'}
              rx="2">
              <title>{weekLabel(d.week_start)}: {d.miles}mi{d.plan_miles ? ` / ${d.plan_miles}mi target` : ''}</title>
            </rect>

            {/* Plan target dot */}
            {d.plan_miles != null && d.plan_miles > 0 && (
              <circle cx={x + barW / 2} cy={toY(d.plan_miles)} r="3"
                fill="#fc4c02" opacity="0.85" />
            )}

            {/* Miles label on current week + any bar > 80% max */}
            {(isCurrent || d.miles > maxVal * 0.75) && (
              <text x={x + barW / 2} y={Math.max(y - 4, padT + 8)} textAnchor="middle"
                fill={isCurrent ? '#C9A84C' : 'rgba(255,255,255,0.55)'}
                fontSize="9" fontWeight={isCurrent ? '700' : '400'} fontFamily="inherit">
                {d.miles}
              </text>
            )}

            {/* X-axis date label (every other week if >8 bars) */}
            {(n <= 8 || i % 2 === 0) && (
              <text x={x + barW / 2} y={H - padB + 16} textAnchor="middle"
                fill="rgba(255,255,255,0.3)" fontSize="8.5" fontFamily="inherit">
                {weekLabel(d.week_start)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

const CadenceChart: React.FC<{ data: CadencePoint[]; mode: 'daily' | 'weekly' | 'monthly' }> = ({ data, mode }) => {
  if (data.length < 2) return <p className="chart-empty">Not enough data</p>;

  const W = 400, H = 160;
  const padL = 40, padR = 16, padT = 24, padB = 32;
  const cW = W - padL - padR;
  const cH = H - padT - padB;
  const vals = data.map(d => d.cadence);
  const minC = Math.min(...vals) - 2;
  const maxC = Math.max(...vals) + 2;
  const n = data.length;

  const toX = (i: number) => padL + (i / Math.max(n - 1, 1)) * cW;
  const toY = (c: number) => padT + cH * (1 - (c - minC) / (maxC - minC));

  const pathD = data.map((d, i) =>
    `${i === 0 ? 'M' : 'L'} ${toX(i).toFixed(1)} ${toY(d.cadence).toFixed(1)}`
  ).join(' ');

  const xLabel = (d: CadencePoint): string => {
    if (mode === 'monthly' && d.month) return d.month.slice(0, 7);
    if (d.date) {
      const dt = new Date(d.date + 'T00:00:00');
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return '';
  };

  // For daily mode with many points, only label every Nth
  const labelEvery = mode === 'daily' ? Math.ceil(n / 5) : 1;

  // Target zone 85–90 spm
  const targetLo = 85, targetHi = 90;
  const showTarget = targetHi > minC && targetLo < maxC;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" aria-label="Cadence trend">
      {/* Target zone */}
      {showTarget && (
        <rect x={padL}
          y={toY(Math.min(targetHi, maxC))}
          width={cW}
          height={Math.abs(toY(targetLo) - toY(targetHi))}
          fill="rgba(34,197,94,0.07)" />
      )}

      {/* Grid */}
      {[minC, (minC + maxC) / 2, maxC].map((c, i) => (
        <g key={i}>
          <line x1={padL} x2={W - padR} y1={toY(c)} y2={toY(c)}
            stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
          <text x={padL - 4} y={toY(c) + 4} textAnchor="end"
            fill="rgba(255,255,255,0.28)" fontSize="9.5" fontFamily="inherit">
            {Math.round(c)}
          </text>
        </g>
      ))}

      {/* Line */}
      <path d={pathD} fill="none" stroke="#60a5fa" strokeWidth="2.5" strokeLinejoin="round" />

      {/* Area under line */}
      <path d={`${pathD} L ${toX(n - 1)} ${padT + cH} L ${padL} ${padT + cH} Z`}
        fill="url(#cadGrad)" opacity="0.15" />
      <defs>
        <linearGradient id="cadGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Dots + values */}
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={toX(i)} cy={toY(d.cadence)} r="5"
            fill="#60a5fa" stroke="#0C0C10" strokeWidth="2">
            <title>{xLabel(d)}: {d.cadence} spm{d.type ? ` · ${d.type}` : ''}</title>
          </circle>
          {(mode !== 'daily' || n <= 7) && (
            <text x={toX(i)} y={toY(d.cadence) - 9} textAnchor="middle"
              fill="#60a5fa" fontSize="9.5" fontWeight="600" fontFamily="inherit">
              {d.cadence}
            </text>
          )}
          {i % labelEvery === 0 && (
            <text x={toX(i)} y={H - padB + 16} textAnchor="middle"
              fill="rgba(255,255,255,0.3)" fontSize="9" fontFamily="inherit">
              {xLabel(d)}
            </text>
          )}
        </g>
      ))}

      {/* Target label */}
      {showTarget && (
        <text x={W - padR} y={toY(87.5) - 4} textAnchor="end"
          fill="rgba(34,197,94,0.55)" fontSize="8.5" fontFamily="inherit">
          target 85–90
        </text>
      )}
    </svg>
  );
};

const PaceHRChart: React.FC<{ data: PaceHRPoint[] }> = ({ data }) => {
  if (data.length < 2) return <p className="chart-empty">Not enough data</p>;

  const W = 400, H = 180;
  const padL = 48, padR = 16, padT = 24, padB = 36;
  const cW = W - padL - padR;
  const cH = H - padT - padB;
  const n = data.length;
  const paces = data.map(d => d.pace_sec);
  const minP = Math.min(...paces) * 0.98;
  const maxP = Math.max(...paces) * 1.02;

  const toX = (i: number) => padL + (i / Math.max(n - 1, 1)) * cW;
  // Higher pace_sec = slower = lower on chart
  const toY = (p: number) => padT + cH * ((p - minP) / (maxP - minP));

  const pathD = data.map((d, i) =>
    `${i === 0 ? 'M' : 'L'} ${toX(i).toFixed(1)} ${toY(d.pace_sec).toFixed(1)}`
  ).join(' ');

  const yTicks = [minP, (minP + maxP) / 2, maxP];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" aria-label="Pace and HR trend">
      {/* Grid */}
      {yTicks.map((p, i) => (
        <g key={i}>
          <line x1={padL} x2={W - padR} y1={toY(p)} y2={toY(p)}
            stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
          <text x={padL - 4} y={toY(p) + 4} textAnchor="end"
            fill="rgba(255,255,255,0.28)" fontSize="9.5" fontFamily="inherit">
            {fmtPace(Math.round(p))}
          </text>
        </g>
      ))}

      {/* "Faster" label */}
      <text x={padL} y={padT - 6} fill="rgba(255,255,255,0.2)" fontSize="8" fontFamily="inherit">
        faster ↑
      </text>

      {/* Line */}
      <path d={pathD} fill="none" stroke="rgba(167,139,250,0.35)" strokeWidth="1.5" />

      {/* Dots colored by HR */}
      {data.map((d, i) => (
        <circle key={i} cx={toX(i)} cy={toY(d.pace_sec)} r="5.5"
          fill={hrColor(d.hr)} stroke="#0C0C10" strokeWidth="1.5">
          <title>{`${d.date}: ${fmtPace(d.pace_sec)}/mi @ HR ${d.hr} (${d.type})`}</title>
        </circle>
      ))}

      {/* X labels every 3rd */}
      {data.map((d, i) =>
        i % 3 === 0 ? (
          <text key={i} x={toX(i)} y={H - padB + 16} textAnchor="middle"
            fill="rgba(255,255,255,0.28)" fontSize="8.5" fontFamily="inherit">
            {weekLabel(d.date)}
          </text>
        ) : null
      )}

      {/* HR legend */}
      {(['< 130', '130–140', '140–150', '150–160', '160+'] as const).map((label, i) => {
        const colors = ['#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444'];
        return (
          <g key={i}>
            <circle cx={padL + i * 60} cy={H - 4} r="4" fill={colors[i]} />
            <text x={padL + i * 60 + 7} y={H - 1}
              fill="rgba(255,255,255,0.3)" fontSize="7.5" fontFamily="inherit">
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ─── Long Run Progression ───────────────────────────────────────────────────

const LongRunProgressionChart: React.FC<{ data: LongRunPoint[] }> = ({ data }) => {
  if (!data.length) return <p className="chart-empty">No long run data yet</p>;

  const W = 700, H = 200;
  const padL = 36, padR = 12, padT = 20, padB = 38;
  const cW = W - padL - padR;
  const cH = H - padT - padB;
  const n = data.length;
  const maxVal = Math.max(...data.map(d => Math.max(d.miles, d.planned_miles || 0)), 1);
  const maxY = maxVal * 1.2;
  const slotW = cW / n;
  const barW = Math.max(slotW * 0.55, 6);

  const toX = (i: number) => padL + i * slotW + (slotW - barW) / 2;
  const toY = (v: number) => padT + cH * (1 - v / maxY);

  const step = maxY > 20 ? 5 : 3;
  const yTicks: number[] = [];
  for (let v = 0; v <= maxY; v += step) yTicks.push(v);

  const planPoints = data
    .map((d, i) => d.planned_miles != null && d.planned_miles > 0
      ? `${toX(i) + barW / 2},${toY(d.planned_miles)}`
      : null)
    .filter(Boolean);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" aria-label="Long run progression">
      {yTicks.map(t => (
        <g key={t}>
          <line x1={padL} x2={W - padR} y1={toY(t)} y2={toY(t)}
            stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
          <text x={padL - 4} y={toY(t) + 4} textAnchor="end"
            fill="rgba(255,255,255,0.28)" fontSize="9.5" fontFamily="inherit">{t}</text>
        </g>
      ))}
      <line x1={padL} x2={W - padR} y1={padT + cH} y2={padT + cH}
        stroke="rgba(255,255,255,0.1)" strokeWidth="1" />

      {planPoints.length > 1 && (
        <polyline points={planPoints.join(' ')} fill="none"
          stroke="#fc4c02" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7" />
      )}

      {data.map((d, i) => {
        const x = toX(i);
        const barH = Math.max(cH * (d.miles / maxY), 2);
        const y = toY(d.miles);
        const isLast = i === n - 1;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={barH}
              fill={isLast ? '#32a2e6' : 'rgba(50,162,230,0.4)'} rx="2">
              <title>{weekLabel(d.week_start)}: {d.miles}mi{d.planned_miles ? ` / ${d.planned_miles}mi planned` : ''}</title>
            </rect>
            {d.planned_miles != null && d.planned_miles > 0 && (
              <circle cx={x + barW / 2} cy={toY(d.planned_miles)} r="3"
                fill="#fc4c02" opacity="0.85" />
            )}
            {(isLast || d.miles >= maxVal * 0.8) && (
              <text x={x + barW / 2} y={Math.max(y - 4, padT + 8)} textAnchor="middle"
                fill={isLast ? '#32a2e6' : 'rgba(255,255,255,0.55)'}
                fontSize="9" fontWeight={isLast ? '700' : '400'} fontFamily="inherit">
                {d.miles}
              </text>
            )}
            {(n <= 8 || i % 2 === 0) && (
              <text x={x + barW / 2} y={H - padB + 16} textAnchor="middle"
                fill="rgba(255,255,255,0.3)" fontSize="8.5" fontFamily="inherit">
                {weekLabel(d.week_start)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// ─── Workout Type Distribution ──────────────────────────────────────────────

const WorkoutTypeDistChart: React.FC<{ data: WorkoutTypeDist[] }> = ({ data }) => {
  if (!data.length) return <p className="chart-empty">No workouts in this period</p>;

  const total = data.reduce((s, d) => s + d.count, 0);
  const TYPE_COLORS: Record<string, string> = {
    'Easy': '#22c55e',
    'Easy Run': '#22c55e',
    'Long Run': '#32a2e6',
    'Tempo': '#f97316',
    'Intervals': '#ef4444',
    'Moderate': '#84cc16',
    'Strength Training': '#a78bfa',
    'Walk': '#14b8a6',
    'Ride': '#06b6d4',
    'Rest': 'rgba(255,255,255,0.18)',
  };
  const color = (t: string) => TYPE_COLORS[t] || '#8b9ab0';

  return (
    <div className="workout-dist-list">
      {data.map(d => {
        const pct = Math.round((d.count / total) * 100);
        return (
          <div key={d.type} className="workout-dist-row">
            <span className="workout-dist-type">{d.type}</span>
            <div className="workout-dist-bar-wrap">
              <div className="workout-dist-bar"
                style={{ width: `${pct}%`, background: color(d.type) }} />
            </div>
            <span className="workout-dist-count">{d.count}</span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Consistency Heatmap ────────────────────────────────────────────────────

const ConsistencyHeatmap: React.FC<{ data: ConsistencyPoint[] }> = ({ data }) => {
  if (!data.length) return <p className="chart-empty">No data</p>;

  const cellSize = 16, gap = 2, monthGap = 6;
  const weeks = 13;

  // Build lookup with type info
  const lookup: Record<string, ConsistencyPoint> = {};
  data.forEach(d => { lookup[d.date] = d; });

  const todayStr = data[data.length - 1]?.date || new Date().toISOString().slice(0, 10);
  const today = new Date(todayStr + 'T00:00:00');
  const gridStart = new Date(today);
  gridStart.setDate(today.getDate() - (weeks * 7 - 1));
  const dow = gridStart.getDay();
  const daysToMon = dow === 0 ? -6 : 1 - dow;
  gridStart.setDate(gridStart.getDate() + daysToMon);

  const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const cells: { col: number; row: number; dateStr: string; point: ConsistencyPoint | null; future: boolean }[] = [];
  const monthMarkers: { col: number; label: string }[] = [];
  const monthStartCols = new Set<number>();
  let lastMonth = -1;

  for (let col = 0; col < weeks; col++) {
    for (let row = 0; row < 7; row++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + col * 7 + row);
      const ds = d.toISOString().slice(0, 10);
      const isFuture = ds > todayStr;
      if (row === 0 && d.getMonth() !== lastMonth) {
        monthMarkers.push({ col, label: MONTH_ABBR[d.getMonth()] });
        if (lastMonth !== -1) monthStartCols.add(col);
        lastMonth = d.getMonth();
      }
      cells.push({ col, row, dateStr: ds, point: lookup[ds] || null, future: isFuture });
    }
  }

  // Compute x offset for each column, adding extra gap at month boundaries
  const colX = (col: number): number => {
    let extra = 0;
    monthStartCols.forEach(mc => { if (col >= mc) extra += monthGap; });
    return 28 + col * (cellSize + gap) + extra;
  };

  const W = colX(weeks - 1) + cellSize + 4;
  const H = 7 * (cellSize + gap) + 36;

  const cellColor = (p: ConsistencyPoint | null, future: boolean): string => {
    if (future) return 'rgba(255,255,255,0.04)';
    if (!p || !p.has_activity) return 'rgba(255,255,255,0.08)';
    if (p.has_run && p.has_strength) return '#32a2e6';  // both = blue
    if (p.has_strength) return '#a78bfa';                // strength = purple
    return '#22c55e';                                     // run = green
  };

  const cellIcon = (p: ConsistencyPoint | null, future: boolean): string | null => {
    if (future || !p || !p.has_activity) return null;
    if (p.has_run && p.has_strength) return '2x';
    if (p.has_strength) return '💪';
    return '🏃';
  };

  const cellTitle = (ds: string, p: ConsistencyPoint | null): string => {
    if (!p || !p.has_activity) return ds;
    const parts = [ds];
    if (p.has_run) parts.push('Run');
    if (p.has_strength) parts.push('Strength');
    return parts.join(' · ');
  };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg consistency-svg" aria-label="Activity consistency heatmap">
      {monthMarkers.map((m, i) => (
        <text key={i} x={colX(m.col)} y={10}
          fill="rgba(255,255,255,0.35)" fontSize="8.5" fontFamily="inherit">
          {m.label}
        </text>
      ))}
      {/* Month separator lines */}
      {Array.from(monthStartCols).map(mc => {
        const sx = colX(mc) - monthGap / 2 - gap / 2;
        return (
          <line key={`sep-${mc}`} x1={sx} y1={14} x2={sx} y2={14 + 7 * (cellSize + gap) - gap}
            stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
        );
      })}
      {DAY_LABELS.map((l, row) => (
        <text key={row} x={22} y={18 + row * (cellSize + gap) + cellSize / 2}
          textAnchor="end" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="inherit">
          {l}
        </text>
      ))}
      {cells.map((c, i) => {
        const icon = cellIcon(c.point, c.future);
        const x = colX(c.col);
        const y = 14 + c.row * (cellSize + gap);
        return (
          <g key={i}>
            <rect x={x} y={y} width={cellSize} height={cellSize} rx="2"
              fill={cellColor(c.point, c.future)}
              opacity={c.future ? 0.5 : 1}>
              <title>{cellTitle(c.dateStr, c.point)}</title>
            </rect>
            {icon && (
              <text x={x + cellSize / 2} y={y + cellSize / 2 + 1}
                textAnchor="middle" dominantBaseline="central"
                fontSize={icon === '2x' ? '6' : '8'} fontFamily="inherit"
                fill={icon === '2x' ? '#fff' : undefined}>
                {icon === '2x' ? '2x' : icon}
              </text>
            )}
          </g>
        );
      })}
      {/* Legend */}
      <text x={28} y={H - 4} fill="rgba(255,255,255,0.3)" fontSize="7.5" fontFamily="inherit">
        🏃 Run
      </text>
      <text x={68} y={H - 4} fill="rgba(255,255,255,0.3)" fontSize="7.5" fontFamily="inherit">
        💪 Strength
      </text>
      <text x={120} y={H - 4} fill="rgba(255,255,255,0.3)" fontSize="7.5" fontFamily="inherit">
        2x Both
      </text>
    </svg>
  );
};

// ─── PR Cards ───────────────────────────────────────────────────────────────

const PR_META: Record<string, { emoji: string; abbr: string }> = {
  '400m':         { emoji: '⚡', abbr: '400m' },
  '1/2 mile':     { emoji: '🏃', abbr: '½mi' },
  '1K':           { emoji: '🏃', abbr: '1K' },
  '1 mile':       { emoji: '🎯', abbr: '1mi' },
  '2 mile':       { emoji: '💨', abbr: '2mi' },
  '5K':           { emoji: '🏅', abbr: '5K' },
  '10K':          { emoji: '🥈', abbr: '10K' },
  'Half-Marathon':{ emoji: '🥇', abbr: '½ Marathon' },
  'Marathon':     { emoji: '🏆', abbr: 'Marathon' },
};

const PRGrid: React.FC<{ efforts: BestEffort[] }> = ({ efforts }) => {
  if (!efforts.length) return <p className="chart-empty">No PR data yet — sync a run with Strava</p>;
  return (
    <div className="pr-grid">
      {efforts.map(e => {
        const meta = PR_META[e.name] || { emoji: '🏃', abbr: e.name };
        return (
          <div key={e.name} className="pr-card">
            <span className="pr-emoji">{meta.emoji}</span>
            <span className="pr-distance">{meta.abbr}</span>
            <span className="pr-time">{fmtTime(e.elapsed_time_s)}</span>
            <span className="pr-date">{e.date}</span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Info tooltip ───────────────────────────────────────────────────────────

const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <span className="info-tip" aria-label={text}>
    <span className="info-tip-icon">ⓘ</span>
    <span className="info-tip-popup">{text}</span>
  </span>
);

// ─── Coach's Take card ──────────────────────────────────────────────────────
// Surfaces the latest WeeklyInsight (AI summary of the past 7 days). Click
// goes to /coach with the follow-up question pre-filled. Hidden if no
// insight has been generated yet.

interface WeeklyInsightData {
  id: number;
  week_start_date: string;
  headline: string;
  follow_up_prompt: string | null;
  created_at: string;
}

const CoachTakeCard: React.FC = () => {
  const [insight, setInsight] = useState<WeeklyInsightData | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getWeeklyInsight();
        setInsight(data);
      } catch {
        // Silent — card just doesn't render if fetch fails or there's no insight
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  if (!loaded || !insight) return null;

  const target = insight.follow_up_prompt
    ? `/coach?prefill=${encodeURIComponent(insight.follow_up_prompt)}`
    : '/coach';

  return (
    <div className="coach-take-card">
      <div className="coach-take-header">
        <span className="coach-take-label">Coach's Take This Week</span>
      </div>
      <div className="coach-take-body">{insight.headline}</div>
      {insight.follow_up_prompt && (
        <Link
          to={target}
          className="coach-take-suggested"
          onClick={() => api.trackEvent('insight_clicked', { insight_id: insight.id })}
        >
          <span className="coach-take-suggested-label">Suggested question to ask:</span>
          <span className="coach-take-suggested-text">"{insight.follow_up_prompt}"</span>
          <span className="coach-take-suggested-cta">Ask coach →</span>
        </Link>
      )}
      {!insight.follow_up_prompt && (
        <Link
          to={target}
          className="coach-take-cta"
          onClick={() => api.trackEvent('insight_clicked', { insight_id: insight.id })}
        >
          Discuss with coach →
        </Link>
      )}
    </div>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────

const PHASE_COLORS: Record<string, string> = {
  Base: '#22c55e', Build: '#eab308', Peak: '#f97316', Taper: '#32a2e6',
};

const PHASE_TIPS: Record<string, string> = {
  Base: 'Building aerobic foundation. Focus on easy miles and consistency.',
  Build: 'Increasing intensity. Quality workouts (tempo, intervals) are key.',
  Peak: 'Highest volume and intensity. Trust the process, prioritize recovery.',
  Taper: 'Reducing volume, maintaining intensity. Trust the training you\'ve done.',
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [charts, setCharts] = useState<DashboardCharts | null>(null);
  const [confidence, setConfidence] = useState<RaceConfidence | null>(null);
  const [intel, setIntel] = useState<DashboardIntelligence | null>(null);
  const [confidenceLoading, setConfidenceLoading] = useState(true);
  const [hrZones, setHrZones] = useState<any>(null);
  const [hrZonesLoading, setHrZonesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [trendsFilter, setTrendsFilter] = useState<7 | 30 | 90>(7);
  const { formatDist, weeklyLabel, formatElev, distLabel, elevLabel,
          inputDistToMiles, inputElevToFeet, milesToDisplay, feetToDisplay } = useUnits();

  // Inline editor for the queued next race (goal time + details were never
  // captured when the race was rolled in — see continue-race).
  const [editingNext, setEditingNext] = useState(false);
  const [savingNext, setSavingNext] = useState(false);
  const [nextForm, setNextForm] = useState({
    race_name: '', race_date: '', distance: '', elevation: '', h: 0, m: 0, s: 0,
  });

  const openNextEditor = () => {
    const g = stats?.next_race_goal_finish_time_seconds || 0;
    setNextForm({
      race_name: stats?.next_race_name || '',
      race_date: stats?.next_race_date || '',
      distance: stats?.next_race_distance_miles != null ? String(milesToDisplay(stats.next_race_distance_miles)) : '',
      elevation: stats?.next_race_elevation_gain_ft != null ? String(feetToDisplay(stats.next_race_elevation_gain_ft)) : '',
      h: Math.floor(g / 3600), m: Math.floor((g % 3600) / 60), s: g % 60,
    });
    setEditingNext(true);
  };

  const saveNextRace = async () => {
    setSavingNext(true);
    try {
      const goalSecs = nextForm.h * 3600 + nextForm.m * 60 + nextForm.s;
      await api.continueIntoNextRace({
        race_name: nextForm.race_name,
        race_date: nextForm.race_date,
        race_distance_miles: inputDistToMiles(parseFloat(nextForm.distance) || 0),
        ...(nextForm.elevation !== '' ? { race_elevation_gain_ft: inputElevToFeet(parseFloat(nextForm.elevation)) } : {}),
        ...(goalSecs > 0 ? { goal_finish_time_seconds: goalSecs } : {}),
      });
      setEditingNext(false);
      await loadStats();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingNext(false);
    }
  };

  useEffect(() => {
    loadStats();
    loadConfidence();
    loadIntelligence();
    api.trackEvent('dashboard_viewed');
  }, []);

  useEffect(() => {
    loadHRZones(trendsFilter);
    loadCharts(trendsFilter);
  }, [trendsFilter]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await api.getDashboardStats();
      setStats(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCharts = async (days: 7 | 30 | 90 = 7) => {
    try {
      setChartsLoading(true);
      const data = await api.getDashboardCharts(days);
      setCharts(data);
    } catch {
      // charts are non-critical — fail silently
    } finally {
      setChartsLoading(false);
    }
  };

  const loadConfidence = async () => {
    try {
      setConfidenceLoading(true);
      const data = await api.getRaceConfidence();
      setConfidence(data);
    } catch {
      // non-critical — fail silently
    } finally {
      setConfidenceLoading(false);
    }
  };

  const loadIntelligence = async () => {
    try {
      const data = await api.getDashboardIntelligence();
      setIntel(data);
    } catch {
      // non-critical
    }
  };

  const loadHRZones = async (days: 7 | 30 | 90 = 90) => {
    try {
      setHrZonesLoading(true);
      const data = await api.getHRZones(days);
      setHrZones(data);
    } catch {
      // non-critical
    } finally {
      setHrZonesLoading(false);
    }
  };

  const handleSyncStrava = async () => {
    try {
      setSyncing(true);
      setSyncMessage(null);
      setError(null);
      const result = await api.syncStravaActivities(90);
      setSyncMessage(
        `✓ Synced! Imported: ${result.imported}, Updated: ${result.updated}, Skipped: ${result.skipped}`
      );
      await loadStats();
      await loadCharts(trendsFilter);
    } catch (err: any) {
      setError(`Sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="dashboard-loading">Loading dashboard...</div>
      </>
    );
  }

  if (error && !stats) {
    return (
      <>
        <Navbar />
        <div className="dashboard-error">Error: {error}</div>
      </>
    );
  }

  const currentWeekMiles = stats?.this_week_mileage ?? 0;
  const currentWeekTSS   = stats?.this_week_tss ?? 0;

  // Rendered in every race-card state (countdown / race day / race complete) —
  // a queued race must stay visible once the near race is behind you, since
  // that's exactly when the plan is about to roll into it.
  const nextRaceSection = (
    <>
      {stats?.next_race_name ? (
        <div className="countdown-next-race">
          Next up after this: <strong>{stats.next_race_name}</strong>
          {stats.next_race_date && (
            <> · {new Date(stats.next_race_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</>
          )}
          <div className="next-race-details">
            {stats.next_race_distance_miles != null && <span>{formatDist(stats.next_race_distance_miles)}</span>}
            {stats.next_race_elevation_gain_ft != null && <span>↑ {formatElev(stats.next_race_elevation_gain_ft)}</span>}
            <span>Goal: {stats.next_race_goal_finish_time_seconds ? fmtTime(stats.next_race_goal_finish_time_seconds) : '—'}</span>
          </div>
          {!editingNext && (
            <button type="button" className="countdown-edit next-race-edit-btn" onClick={openNextEditor}>
              {stats.next_race_goal_finish_time_seconds ? 'Edit next race →' : 'Add goal & details →'}
            </button>
          )}
        </div>
      ) : (stats?.days_until_race !== undefined && stats.days_until_race <= 14) && (
        <Link to="/races" className="countdown-continue">Got a next race? Roll into it →</Link>
      )}

      {editingNext && (
        <div className="next-race-editor">
          <label>Race name
            <input type="text" value={nextForm.race_name}
              onChange={e => setNextForm({ ...nextForm, race_name: e.target.value })} />
          </label>
          <label>Race date
            <input type="date" value={nextForm.race_date}
              onChange={e => setNextForm({ ...nextForm, race_date: e.target.value })} />
          </label>
          <div className="next-race-editor-row">
            <label>Distance ({distLabel})
              <input type="number" step="0.1" min="0" value={nextForm.distance}
                onChange={e => setNextForm({ ...nextForm, distance: e.target.value })} />
            </label>
            <label>Elevation ({elevLabel})
              <input type="number" min="0" value={nextForm.elevation}
                onChange={e => setNextForm({ ...nextForm, elevation: e.target.value })} />
            </label>
          </div>
          <div className="next-race-editor-goal">
            <span>Goal time</span>
            <input type="number" min="0" aria-label="hours" value={nextForm.h}
              onChange={e => setNextForm({ ...nextForm, h: parseInt(e.target.value) || 0 })} />:
            <input type="number" min="0" max="59" aria-label="minutes" value={nextForm.m}
              onChange={e => setNextForm({ ...nextForm, m: parseInt(e.target.value) || 0 })} />:
            <input type="number" min="0" max="59" aria-label="seconds" value={nextForm.s}
              onChange={e => setNextForm({ ...nextForm, s: parseInt(e.target.value) || 0 })} />
          </div>
          <div className="next-race-editor-actions">
            <button type="button" onClick={() => setEditingNext(false)} disabled={savingNext}>Cancel</button>
            <button type="button" className="primary"
              onClick={saveNextRace}
              disabled={savingNext || !nextForm.race_name || !nextForm.race_date || !nextForm.distance}>
              {savingNext ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <Navbar />
      <div className="dashboard">
        <div className="dashboard-container">

          {/* ── Header ── */}
          <div className="dashboard-header">
            <h1>Dashboard</h1>
            <button onClick={handleSyncStrava} className="btn-sync-strava"
              disabled={syncing} aria-label="Sync activities from Strava">
              {syncing ? (
                <svg width="220" height="40" viewBox="0 0 220 40">
                  <rect width="220" height="40" rx="4" fill="#FC4C02" />
                  <path d="M22 13L17.5 23L13 13H9.5L16.5 28H21L28 13H22Z" fill="white" opacity="0.6" />
                  <text x="38" y="26" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="white" opacity="0.6">Syncing with STRAVA...</text>
                </svg>
              ) : (
                <svg width="200" height="40" viewBox="0 0 200 40">
                  <rect width="200" height="40" rx="4" fill="#FC4C02" />
                  <path d="M22 13L17.5 23L13 13H9.5L16.5 28H21L28 13H22Z" fill="white" />
                  <text x="38" y="26" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="white">Sync with STRAVA</text>
                </svg>
              )}
            </button>
          </div>

          {syncMessage && <div className="sync-success-message">{syncMessage}</div>}
          {error && <div className="sync-error-message">{error}</div>}

          {/* ═══════════════════════════════════════════════ */}
          {/* ── TOP ROW: Race + Stats (3 columns) ─────── */}
          {/* ═══════════════════════════════════════════════ */}
          <div className="dashboard-hero-row">
            {stats?.race_name && stats?.days_until_race !== undefined ? (
              stats.days_until_race < 0 ? (
                <div className="race-countdown-card race-complete-card">
                  <div className="race-complete-icon">🏁</div>
                  <div className="countdown-label">Race complete</div>
                  <div className="countdown-race">{stats.race_name}</div>
                  {stats.next_race_name ? (
                    <>
                      <p className="race-complete-text">
                        Hope it went well! Your plan rolls into your next race from the coming Monday.
                      </p>
                      {nextRaceSection}
                    </>
                  ) : (
                    <>
                      <p className="race-complete-text">
                        Hope it went well! Ready for the next one?
                      </p>
                      <Link to="/races" className="btn-primary">Continue into next race</Link>
                      <Link to="/race-goal" className="countdown-edit">Or set one manually →</Link>
                    </>
                  )}
                </div>
              ) : stats.days_until_race === 0 ? (
                <div className="race-countdown-card">
                  <div className="countdown-number">🏃</div>
                  <div className="countdown-label">Race day!</div>
                  <div className="countdown-race">{stats.race_name}</div>
                  {stats.goal_time && (
                    <div className="countdown-goal">Target: {stats.goal_time}</div>
                  )}
                  <Link to="/race-goal" className="countdown-edit">Edit Goal →</Link>
                  {nextRaceSection}
                </div>
              ) : (
                <div className="race-countdown-card">
                  <div className="countdown-number">{stats.days_until_race}</div>
                  <div className="countdown-label">days until</div>
                  <div className="countdown-race">{stats.race_name}</div>
                  {stats.goal_time && (
                    <div className="countdown-goal">Target: {stats.goal_time}</div>
                  )}
                  <ConfidenceBand
                    data={confidence}
                    loading={confidenceLoading}
                    goalTime={stats?.goal_time}
                  />
                  {stats.goal_time ? (
                    <Link to="/race-goal" className="countdown-edit">Edit Goal →</Link>
                  ) : (
                    <Link to="/race-goal" className="countdown-edit countdown-goal-missing">⚠ Set your race goal →</Link>
                  )}
                  {nextRaceSection}
                </div>
              )
            ) : (
              <div className="no-race-card">
                <div className="no-race-icon">🎯</div>
                <h2>Set Your Race Goal</h2>
                <p>Get a personalized training plan built around your next race.</p>
                <Link to="/race-goal" className="btn-primary">Set Race Goal</Link>
              </div>
            )}

            <div className="stats-column">
              <div className="stat-card">
                <div className="stat-label">
                  This Week
                  <InfoTip text="Total miles from your Strava runs this week (Mon–Sun). The color dot reflects how this week's projected mileage compares to a typical band for your race distance and current training phase." />
                </div>
                <div className="stat-value">
                  {formatDist(currentWeekMiles)}
                  {stats?.mileage_verdict_color && (
                    <span
                      className={`tss-dot tss-dot-${stats.mileage_verdict_color}`}
                      title={`${stats.mileage_verdict_label} for ${stats.tss_phase} phase`}
                    />
                  )}
                  {stats?.mileage_verdict_label && (
                    <span className={`tss-verdict-label tss-verdict-${stats.mileage_verdict_color}`}>
                      {stats.mileage_verdict_label}
                    </span>
                  )}
                </div>
                {stats?.mileage_projection != null && stats.mileage_projection !== Math.round(currentWeekMiles * 10) / 10 && (
                  <div className="stat-sublabel">On pace for {formatDist(stats.mileage_projection)} by Sunday</div>
                )}
                {stats?.has_plan_target && stats?.mileage_plan_target != null ? (
                  <div className="stat-sublabel">
                    Plan target: {formatDist(stats.mileage_plan_target)}
                  </div>
                ) : (stats?.mileage_band_low != null && stats?.mileage_band_high != null && stats?.tss_phase ? (
                  <div className="stat-sublabel">
                    {stats.tss_phase} phase typical: {formatDist(stats.mileage_band_low)}–{formatDist(stats.mileage_band_high)}
                  </div>
                ) : (
                  <div className="stat-sublabel">{weeklyLabel}</div>
                ))}
              </div>
              <div className="stat-card">
                <div className="stat-label">
                  Weekly TSS
                  <InfoTip text="Training Stress Score — combines duration and intensity into a single load number. A hard 1-hour tempo = ~100 TSS. Easy 1-hour run = ~50 TSS. The color dot reflects how this week's projected load compares to a typical band for your race distance and current training phase." />
                </div>
                <div className="stat-value">
                  {currentWeekTSS}
                  {stats?.tss_verdict_color && (
                    <span
                      className={`tss-dot tss-dot-${stats.tss_verdict_color}`}
                      title={`${stats.tss_verdict_label} for ${stats.tss_phase} phase`}
                    />
                  )}
                  {stats?.tss_verdict_label && (
                    <span className={`tss-verdict-label tss-verdict-${stats.tss_verdict_color}`}>
                      {stats.tss_verdict_label}
                    </span>
                  )}
                </div>
                {stats?.tss_projection != null && stats.tss_projection !== currentWeekTSS && (
                  <div className="stat-sublabel">On pace for {stats.tss_projection} by Sunday</div>
                )}
                {stats?.has_plan_target && stats?.tss_plan_target != null ? (
                  <div className="stat-sublabel">
                    Plan target: {stats.tss_plan_target} TSS
                  </div>
                ) : (stats?.tss_band_low != null && stats?.tss_band_high != null && stats?.tss_phase && (
                  <div className="stat-sublabel">
                    {stats.tss_phase} phase typical: {stats.tss_band_low}–{stats.tss_band_high}
                  </div>
                ))}
                {stats?.load_advice && (
                  <div className="load-advice-note">{stats.load_advice}</div>
                )}
              </div>
            </div>

            <div className="stats-column">
              <div className="stat-card">
                <div className="stat-label">
                  Last 30 Days
                  <InfoTip text="Total running distance from Strava over the past 30 days. Strength workouts excluded." />
                </div>
                <div className="stat-value">{formatDist(stats?.last_30_days_miles || 0)}</div>
                <div className="stat-sublabel">total distance</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">
                  Time Running
                  <InfoTip text="Total elapsed run time from Strava in the past 30 days." />
                </div>
                <div className="stat-value">{stats?.total_hours_30_days || 0}h</div>
                <div className="stat-sublabel">last 30 days</div>
              </div>
            </div>
          </div>

          <WeekPlanCard />

          <CoachTakeCard />

          {/* ═══════════════════════════════════════════════ */}
          {/* ── Volume & Progression ──────────────────── */}
          {/* ═══════════════════════════════════════════════ */}
          <div className="dashboard-section">
            <h2 className="dashboard-section-title">Training Volume</h2>

            <div className="chart-card">
              <div className="chart-card-header">
                <div>
                  <h2 className="chart-title">
                    Weekly Training Volume
                    <InfoTip text="Miles per week from your Strava runs vs. your training plan target. The orange line shows your plan's weekly mileage goal." />
                  </h2>
                  <p className="chart-subtitle">Miles per week · Last 16 weeks</p>
                </div>
                <div className="chart-legend">
                  <span className="legend-dot" style={{ background: '#C9A84C' }} /> Actual
                  <span className="legend-dot" style={{ background: '#fc4c02' }} /> Target
                </div>
              </div>
              {chartsLoading ? (
                <div className="chart-skeleton" />
              ) : (
                <VolumeChart data={charts?.weekly_volume || []} />
              )}
            </div>

            <div className="chart-card">
              <div className="chart-card-header">
                <div>
                  <h2 className="chart-title">
                    Long Run Progression
                    <InfoTip text="Your longest run each week from Strava vs. what your training plan prescribed. Helps track endurance buildup toward race distance." />
                  </h2>
                  <p className="chart-subtitle">Longest run per week · last 12 weeks</p>
                </div>
                <div className="chart-legend">
                  <span className="legend-dot" style={{ background: '#32a2e6' }} /> Actual
                  <span className="legend-dot" style={{ background: '#fc4c02' }} /> Planned
                </div>
              </div>
              {chartsLoading ? (
                <div className="chart-skeleton" />
              ) : (
                <LongRunProgressionChart data={charts?.long_run_progression || []} />
              )}
            </div>

            {/* Workout Mix + Consistency */}
            <div className="metrics-row">
              <div className="chart-card">
                <div className="chart-card-header">
                  <div>
                    <h2 className="chart-title">
                      Workout Mix
                      <InfoTip text="Breakdown of workout types from your training plan and Strava data. A healthy mix has ~80% easy runs and ~20% quality work." />
                    </h2>
                    <p className="chart-subtitle">Workouts by type · last 90 days</p>
                  </div>
                </div>
                {chartsLoading ? (
                  <div className="chart-skeleton" />
                ) : (
                  <WorkoutTypeDistChart data={charts?.workout_type_dist || []} />
                )}
              </div>

              <div className="chart-card">
                <div className="chart-card-header">
                  <div>
                    <h2 className="chart-title">
                      Consistency
                      <InfoTip text="Daily activity log from Strava. Green = run, purple = strength, blue = both. Consistency is the #1 predictor of race performance." />
                    </h2>
                    <p className="chart-subtitle">Activity heatmap · last 90 days</p>
                  </div>
                </div>
                {chartsLoading ? (
                  <div className="chart-skeleton" />
                ) : (
                  <ConsistencyHeatmap data={intel?.consistency_enhanced || charts?.consistency || []} />
                )}
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════ */}
          {/* ── SECTION 3: Training Health ─────────────── */}
          {/* ═══════════════════════════════════════════════ */}
          <div className="dashboard-section">
            <div className="training-health-header">
              <h2 className="dashboard-section-title">Training Health</h2>
              <div className="trends-filter-row trends-filter-row--inline">
                <span className="trends-filter-label">Showing:</span>
                {([7, 30, 90] as const).map(d => (
                  <button key={d}
                    className={`trends-pill${trendsFilter === d ? ' active' : ''}`}
                    onClick={() => setTrendsFilter(d)}>
                    {d}d
                  </button>
                ))}
              </div>
            </div>

            {/* Heart Rate Zones */}
            <div className="chart-card">
              <div className="chart-card-header">
                <div>
                  <h2 className="chart-title">
                    Heart Rate Zones
                    <InfoTip text="Zones are anchored on your LTHR (lactate-threshold HR) — the highest HR you've sustained for 20 minutes across your training history — once you have enough data. Until then we fall back to %max-HR. Time-in-zone is computed from per-lap HR." />
                  </h2>
                  <p className="chart-subtitle">
                    {hrZones?.available
                      ? [
                          'Time-in-zone',
                          hrZones.max_hr ? `max HR ${hrZones.max_hr} bpm` : null,
                          hrZones.lthr ? `LTHR ${hrZones.lthr} bpm` : null,
                          `${hrZones.total_runs} runs`,
                          `${hrZones.total_minutes ?? 0} min total`,
                        ].filter(Boolean).join(' · ')
                      : 'Zone distribution'}
                  </p>
                </div>
              </div>
              {!hrZones || !hrZones.available ? (
                <div className="hr-zones-empty">
                  <p>Heart rate zones cannot be calculated — no max heart rate data available.</p>
                  <Link to="/race-goal" className="hr-zones-link">Set your max heart rate →</Link>
                </div>
              ) : hrZonesLoading ? (
                <div className="chart-skeleton" style={{ height: 200 }} />
              ) : hrZones.total_minutes === 0 ? (
                <div className="hr-zones-empty">
                  <p>No heart rate data in the last {hrZones.days} days.</p>
                </div>
              ) : (
                <div className="hr-zones-grid">
                  {hrZones.zones.map((z: any) => {
                    const maxPct = Math.max(...hrZones.zones.map((x: any) => x.pct), 1);
                    return (
                      <div key={z.zone} className="hr-zone-row">
                        <div className="hr-zone-label">
                          <span className="hr-zone-dot" style={{ background: z.color }} />
                          <span className="hr-zone-name">Z{z.zone} {z.name}</span>
                          <span className="hr-zone-bpm">{z.min}–{z.max} bpm</span>
                        </div>
                        <div className="hr-zone-bar-wrap">
                          <div className="hr-zone-bar"
                            style={{
                              width: `${Math.max((z.pct / maxPct) * 100, z.pct > 0 ? 8 : 0)}%`,
                              background: z.color,
                            }}
                          />
                        </div>
                        <span className="hr-zone-count">
                          {z.minutes > 0 ? `${z.minutes}m (${z.pct}%)` : '—'}
                        </span>
                      </div>
                    );
                  })}
                {hrZones.recommendation && (
                  <div className={`hr-zones-rec hr-zones-rec--${hrZones.recommendation.type}`}>
                    <span className="hr-zones-rec-icon">
                      {hrZones.recommendation.type === 'success' ? '✓' :
                       hrZones.recommendation.type === 'warning' ? '⚠' :
                       hrZones.recommendation.type === 'caution' ? '!' : 'ℹ'}
                    </span>
                    <span className="hr-zones-rec-text">{hrZones.recommendation.text}</span>
                  </div>
                )}
                </div>
              )}
            </div>

            {/* Cadence + Pace-HR */}
            <div className="metrics-row">
              <div className="chart-card">
                <div className="chart-card-header">
                  <div>
                    <h2 className="chart-title">
                      Cadence Trend
                      <InfoTip text="Cadence = steps per minute (both feet). The green band marks 85–90 spm — the range where most runners become more efficient and reduce injury risk. Higher cadence = shorter ground contact time." />
                    </h2>
                    <p className="chart-subtitle">
                      Avg steps/min ·{' '}
                      {trendsFilter === 7 ? 'individual runs' : trendsFilter === 30 ? 'weekly avg' : 'monthly avg'}
                    </p>
                  </div>
                </div>
                {chartsLoading ? (
                  <div className="chart-skeleton" />
                ) : (
                  <CadenceChart
                    data={charts?.cadence_trend || []}
                    mode={charts?.cadence_mode || 'monthly'}
                  />
                )}
              </div>

              <div className="chart-card">
                <div className="chart-card-header">
                  <div>
                    <h2 className="chart-title">
                      Pace & Heart Rate
                      <InfoTip text="Each dot is one run. Dot color shows your heart rate zone. As fitness improves, you run the same pace at a lower HR — dots shift greener over time at the same vertical position." />
                    </h2>
                    <p className="chart-subtitle">Easy runs · dot color = HR zone</p>
                  </div>
                </div>
                {chartsLoading ? (
                  <div className="chart-skeleton" />
                ) : (
                  <PaceHRChart data={charts?.pace_hr || []} />
                )}
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════ */}
          {/* ── SECTION 4: Plan Intelligence ───────────── */}
          {/* ═══════════════════════════════════════════════ */}
          {intel && (
            <div className="dashboard-section">
              <h2 className="dashboard-section-title">Plan Intelligence</h2>

              {/* Phase + Recovery row */}
              <div className="intel-row">
                {/* Training Phase */}
                {intel.phase.phase && (
                  <div className="chart-card intel-phase-card">
                    <div className="chart-card-header">
                      <h2 className="chart-title">Training Phase</h2>
                    </div>
                    <div className="phase-badge-row">
                      <span className="phase-badge" style={{
                        background: `${PHASE_COLORS[intel.phase.phase] || '#7A9BB5'}18`,
                        color: PHASE_COLORS[intel.phase.phase] || '#7A9BB5',
                        borderColor: `${PHASE_COLORS[intel.phase.phase] || '#7A9BB5'}40`,
                      }}>
                        {intel.phase.phase.toUpperCase()}
                      </span>
                      <div className="phase-meta">
                        {intel.phase.week_number != null && intel.phase.total_weeks != null && (
                          <span>Week {intel.phase.week_number} of {intel.phase.total_weeks}</span>
                        )}
                        {intel.phase.weeks_until_race != null && (
                          <span className="phase-race-weeks">{intel.phase.weeks_until_race} weeks to race</span>
                        )}
                      </div>
                    </div>
                    <p className="phase-tip">{PHASE_TIPS[intel.phase.phase] || ''}</p>
                  </div>
                )}

                {/* Recovery Status */}
                <div className="chart-card intel-recovery-card">
                  <div className="chart-card-header">
                    <h2 className="chart-title">Recovery Status</h2>
                  </div>
                  <div className="recovery-grid">
                    <div className="recovery-item">
                      <div className="recovery-label">TSS Trend</div>
                      <div className="recovery-sparkline">
                        {intel.recovery.tss_weeks.map((w, i) => {
                          const maxTss = Math.max(...intel.recovery.tss_weeks.map(t => t.tss), 1);
                          return (
                            <div key={i} className="recovery-tss-bar"
                              style={{ height: `${Math.max((w.tss / maxTss) * 100, 8)}%` }}
                              title={`${w.tss} TSS`} />
                          );
                        })}
                      </div>
                      <div className={`recovery-value recovery-${intel.recovery.tss_trend === 'rising' ? 'warn' : 'good'}`}>
                        {intel.recovery.tss_trend === 'rising' ? 'Rising' :
                         intel.recovery.tss_trend === 'falling' ? 'Falling' : 'Stable'}
                      </div>
                    </div>
                    <div className="recovery-item">
                      <div className="recovery-label">Fatigue</div>
                      <div className={`recovery-value ${intel.recovery.fatigue_flag ? 'recovery-warn' : 'recovery-good'}`}>
                        {intel.recovery.fatigue_flag ? 'Flag active' : 'No flag'}
                      </div>
                    </div>
                    <div className="recovery-item">
                      <div className="recovery-label">Last Hard Effort</div>
                      <div className="recovery-value">
                        {intel.recovery.days_since_hard_effort != null
                          ? `${intel.recovery.days_since_hard_effort}d ago`
                          : 'N/A'}
                      </div>
                    </div>
                    <div className="recovery-item">
                      <div className="recovery-label">Last Long Run</div>
                      <div className="recovery-value">
                        {intel.recovery.days_since_long_effort != null
                          ? `${intel.recovery.days_since_long_effort}d ago`
                          : 'N/A'}
                      </div>
                    </div>
                    <div className="recovery-item">
                      <div className="recovery-label">Rest Violations</div>
                      <div className={`recovery-value ${intel.recovery.rest_violations_2wk > 0 ? 'recovery-warn' : 'recovery-good'}`}>
                        {intel.recovery.rest_violations_2wk} (2 wk)
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Alerts */}
              {intel.alerts.length > 0 && (
                <div className="chart-card intel-alerts-card">
                  <div className="chart-card-header">
                    <h2 className="chart-title">Active Alerts</h2>
                  </div>
                  {intel.alerts.map(a => (
                    <div key={a.id} className={`alert-item alert-${a.severity || 'mild'}`}>
                      <span className="alert-icon">
                        {a.severity === 'severe' ? '🚨' : a.severity === 'moderate' ? '⚠️' : '🦵'}
                      </span>
                      <div className="alert-text">
                        <div className="alert-title">
                          {a.body_part || a.type} — {a.severity || 'noted'}
                        </div>
                        <div className="alert-meta">
                          {a.description} · {a.days_active}d ago
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════ */}
          {/* ── Personal Records (bottom) ─────────────── */}
          {/* ═══════════════════════════════════════════════ */}
          <div className="dashboard-section">
            <h2 className="dashboard-section-title">Personal Records</h2>
            <div className="chart-card">
              <div className="chart-card-header">
                <div>
                  <h2 className="chart-title">
                    Best Efforts
                    <InfoTip text="Best efforts detected by Strava across standard race distances. Updated automatically each time you sync." />
                  </h2>
                  <p className="chart-subtitle">Best efforts from available Strava data</p>
                </div>
              </div>
              {chartsLoading ? (
                <div className="chart-skeleton" style={{ height: 80 }} />
              ) : (
                <PRGrid efforts={charts?.best_efforts || []} />
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Dashboard;
